import React, { useEffect, useRef, useState } from "react";
import classes from "./JobDetail.module.scss";
import { Button } from "../Button/Button";
import { ReactComponent as RightArrowIcon } from "../../assets/icons/arrow-right.svg";
import { useHistory } from "react-router-dom";
import isMobile from "../../helpers/isMobile";
import { useQuery } from "react-query";
import { IBusinessAreaFields, IJobsSearchJobDetailsFields } from "../../@types/contentful";
import { SharePopUp } from "../SharePopUp/SharePopUp";
import { fetchJob } from "../../helpers/job";
import { scrollToStart } from "../../helpers/scrollTo";
import { Link } from "react-router-dom";
import { SanitizeHTML } from "../SanitizeHtml/SanitizeHtml";

interface Props {
  jobId: string;
  contentfulData: IJobsSearchJobDetailsFields;
  contentfulJobCategories: IBusinessAreaFields[];
  [x: string]: any;
}

export const JobDetail: React.FC<Props> = ({ jobId, contentfulData, contentfulJobCategories, ...props }) => {
    const {
      data: jobData,
      isError: jobDataError,
      isFetching: jobDataFetching,
      isFetched: jobDataFetched,
    } = useQuery(["jobDescription", { jobId }], fetchJob, {
      refetchOnWindowFocus: false,
      retry: false,
    });
    const {
      region,
      timeType,
      group,
      objectID,
      type,
      experienceLevel,
      travelRequired,
      relocationAvailable,
      summary = "",
      description,
      advert,
      detailLink,
      title,
      source,
    }: JobDescription = jobData ? jobData : {};

    const { push, goBack, location, ...historyProps } = useHistory();
    const [showTopPopUp, setShowTopPopUp] = useState<boolean>(false);
    const [showBottomPopUp, setShowBottomPopUp] = useState<boolean>(false);
    const sharePopUpData = contentfulData?.sharePopUp?.fields;
    const replacedGroup = contentfulJobCategories?.find(
      (category) => category?.tag?.toLowerCase() === group?.toLowerCase()
    );
    const jobDescriptionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (jobData) {
        scrollToStart(jobDescriptionRef);
      }
    }, [jobData]);

    useEffect(() => {
      if(isMobile() || window.innerWidth < 768) {
        if(showTopPopUp || showBottomPopUp) {
          window.document.body.classList.add('noscroll');
        } else {
          window.document.body.classList.remove('noscroll');
        }
      }
    }, [showTopPopUp, showBottomPopUp]);

    if (jobDataError) {
      return (
        <div ref={jobDescriptionRef}>
          <h1 className={classes.jobDescriptionTitle}>
            { contentfulData?.jobNotAvailable }
          </h1>
          <Button
            style={{ display: 'inline-block' }}
            href={contentfulData?.seeAllAvailableJobsUrl}
            variant="link"
            rounded
          >
            { contentfulData?.seeAllAvailableJobsButton }
          </Button>
        </div>
      )
    }

    if (jobData) {
      const additionalUrlParams = (!source || source === "brassring")
         ? contentfulData?.additionalBrassRingUrlParameters 
         : "";
      return (
        <div
          ref={jobDescriptionRef}
          className={classes.jobDescriptionWrapper}
        >
          <Link
            className={classes.jobDescriptionBackButton}
            to={{
              search: location.state ? (location.state as string).split('/').pop() : '',
              state: { goBack: true },
            }}
          >
            <div className={classes.leftArrow}>
              <RightArrowIcon />
            </div>
            <span>{ contentfulData?.backToSearchButton }</span>
          </Link>
          <h1
            className={classes.jobDescriptionTitle}
          >
            <SanitizeHTML html={title} />
          </h1>
          <div className={classes.jobDescriptionDetails}>
            <div className={classes.jobDescriptionRow}>
              <div className={classes.jobDescriptionItem}>
                <div className={classes.jobDescriptionItemTitle}>{ contentfulData?.location }</div>
                <div className={classes.jobDescriptionItemInfo}>{ region ? region : '...' }</div>
              </div>
              <div className={classes.jobDescriptionItem}>
                <div className={classes.jobDescriptionItemTitle}>{ contentfulData?.travelRequired }</div>
                <div className={classes.jobDescriptionItemInfo}>{ travelRequired ? travelRequired : '...' }</div>
              </div>
            </div>
            <div className={classes.jobDescriptionRow}>
              <div className={classes.jobDescriptionItem}>
                <div className={classes.jobDescriptionItemTitle}>{ contentfulData?.jobCategory }</div>
                <div className={classes.jobDescriptionItemInfo}>
                  { replacedGroup ? replacedGroup?.label : group }
                </div>
              </div>
              <div className={classes.jobDescriptionItem}>
                <div className={classes.jobDescriptionItemTitle}>{ contentfulData?.relocationAvailable }</div>
                <div className={classes.jobDescriptionItemInfo}>{ relocationAvailable ? relocationAvailable : '...' }</div>
              </div>
            </div>
            <div className={classes.jobDescriptionRow}>
              <div className={classes.jobDescriptionItem}>
                <div className={classes.jobDescriptionItemTitle}>{ contentfulData?.jobType }</div>
                <div className={classes.jobDescriptionItemInfo}>{ type ? type : '...'}</div>
              </div>
              <div className={classes.jobDescriptionItem}>
                <div className={classes.jobDescriptionItemTitle}>{ contentfulData?.jobCode }</div>
                <div className={classes.jobDescriptionItemInfo}>{ objectID ? objectID : '...' }</div>
              </div>
            </div>
            <div className={classes.jobDescriptionRow}>
              <div className={classes.jobDescriptionItem}>
                <div className={classes.jobDescriptionItemTitle}>{ contentfulData?.experienceLevel }</div>
                <div className={classes.jobDescriptionItemInfo}>{ experienceLevel ? experienceLevel : '...' }</div>
              </div>
            </div>
          </div>
          <div className={classes.jobDescriptionActions}>
            <Button
              rounded
              target="_blank"
              variant="link"
              href={`${detailLink}${additionalUrlParams}`}
              className={classes.jobDescriptionButton}
            >
              { contentfulData?.applyButton }
            </Button>
            <div style={{ position: 'relative' }}>
              <Button
                onClick={() => setShowTopPopUp(true)}
                rounded
                className={classes.jobDescriptionShareButton}
              >
                { contentfulData?.shareLink }
              </Button>
              { showTopPopUp && (
                <SharePopUp
                  className={classes.sharePopUp}
                  title={sharePopUpData?.title as string}
                  emailSubject={sharePopUpData?.emailSubject as string}
                  emailShare={sharePopUpData?.emailShare as string}
                  jobLink={sharePopUpData?.jobLink as string}
                  linkCopy={sharePopUpData?.linkCopy as string}
                  doneButton={sharePopUpData?.doneButton as string}
                  setShowPopUp={setShowTopPopUp}
                />
              ) }
            </div>
          </div>
          <div className={classes.jobDescriptionContent}>
            <div>
              {contentfulData?.jobSummary && (
                <h2>
                  { contentfulData?.jobSummary }
                </h2>
              )}
              { !!summary && (!source || source === "brassring") && (
                <>
                  <div>
                    <SanitizeHTML html={summary} />
                  </div>
                  <br />
                  <br />
                </>
              )}
              { advert?.length > 10 && <div> <SanitizeHTML html={advert} /> </div>}
              { description?.length > 10 && <div> <SanitizeHTML html={description} /> </div> }
            </div>
          </div>
          <div className={classes.jobDescriptionActions}>
            <Button
              rounded
              target="_blank"
              href={`${detailLink}${additionalUrlParams}`}
              variant="link"
              className={classes.jobDescriptionButton}
            >
              { contentfulData?.applyButton }
            </Button>
            <div style={{ position: 'relative' }}>
              <Button
                onClick={() => setShowBottomPopUp(true)}
                rounded
                className={classes.jobDescriptionShareButton}
              >
                { contentfulData?.shareLink }
              </Button>
              { showBottomPopUp && (
                <SharePopUp
                  className={classes.sharePopUp}
                  title={sharePopUpData?.title as string}
                  emailSubject={sharePopUpData?.emailSubject as string}
                  emailShare={sharePopUpData?.emailShare as string}
                  jobLink={sharePopUpData?.jobLink as string}
                  linkCopy={sharePopUpData?.linkCopy as string}
                  doneButton={sharePopUpData?.doneButton as string}
                  setShowPopUp={setShowBottomPopUp}
                />
              ) }
            </div>
          </div>
          <Link
            className={classes.jobDescriptionBackButton}
            to={{
              search: location.state ? (location.state as string).split('/').pop() : '',
              state: { goBack: true },
            }}
          >
            <div className={classes.leftArrow}>
              <RightArrowIcon />
            </div>
            <span>{ contentfulData?.backToSearchButton }</span>
          </Link>
        </div>
      );
    }

    return <div ref={jobDescriptionRef} />;
  };
